<template>
    <tr :style="{ backgroundColor: statusBgColor }" @click="makeLink">
        <td v-if="isForecastManager || isZonesSupervisor" class="check" v-on:click.stop>
            <CheckboxTable :value="isSelected()" :forecastId="forecast.id" @updateValue="addSelect"></CheckboxTable>
        </td>
        <td class="id px-2">
            <div>
                <span> {{ forecast.id }} </span>
            </div>
        </td>
        <td class="ref pl-2 pr-4">{{ forecast.reference }}</td>
        <td class="name px-2">{{ forecast.name }}</td>
        <td class="calendar px-2"><DateInfosSimple :forecast="forecast" :forecast-window="window"></DateInfosSimple></td>
        <td class="markets px-2" v-if="window == null">{{ marketsSubmitted }}</td>
        <td class="items px-2">{{ items }}</td>
        <td class="approved px-2">
            <template v-if="isApproved">
                <ApprovedIcon></ApprovedIcon>
            </template>
        </td>
        <td class="xls px-2" v-if="window == null || displayXlsExport" v-on:click.stop>
            <template v-if="isXlsExist">
                <div v-on:click="exportXls(forecast)" class="d-flex align-items-center justify-content-center">
                    <XlsExportIcon :color="statusColor"></XlsExportIcon>
                </div>
            </template>
        </td>
        <td class="status px-2" :style="{ backgroundColor: statusColor }">{{ statusName }}</td>
    </tr>
</template>
<script>
import { FORECAST_STATUS, FORECAST_WINDOW_STATUS, RECURRENCE_TYPE } from "@/components/forecasts/constants";
import { ROUTES } from "@/components/forecasts/router";
import { mapGetters } from "vuex";
import ForecastStatus from "@/components/forecasts/elements/ForecastStatus";
import { getStatusBgColor, getStatusColor, getStatusName } from "@/components/forecasts/utils/utilis";
import DateInfo from "@/components/forecasts/elements/DateInfo";
import MarketIcon from "@/components/forecasts/elements/icons/MarketIcon";
import ItemsIcon from "@/components/forecasts/elements/icons/ItemsIcon";
import { formatDate as fDate } from "@/components/forecasts/utils/utilis";
import { PROFILES } from "@/plugins/AccessUserPlugin";
import MultiLineText from "@/components/forecasts/elements/general/MultiLineText";
import ForecastNameBanner from "@/components/forecasts/elements/general/ForecastNameBanner";
import forecastsService from "../forecastsService";
import TryLoad from "@/mixins/TryLoadMixin";
import CheckboxTable from "@/components/forecasts/elements/form/CheckBoxTable";
import DateInfosSimple from "@/components/forecasts/elements/general/DateInfosSimple";
import ApprovedIcon from "@/components/forecasts/elements/icons/ApprovedIcon";
import Globals from "@/components/globals";
import XlsExportIcon from "@/components/forecasts/elements/icons/XlsExportIcon";

export default {
    mixins: [TryLoad],
    components: {
        ForecastNameBanner,
        MultiLineText,
        ItemsIcon,
        MarketIcon,
        DateInfo,
        ForecastStatus,
        CheckboxTable,
        DateInfosSimple,
        ApprovedIcon,
        XlsExportIcon
    },
    props: ["forecast", "forecastSelected", "window", "display-xls-export"],
    data() {
        return {
            RECURRENCE_TYPE,
            ROUTES,
            selected: false,
            loadingXls: false
        };
    },
    computed: {
        ...mapGetters("brand", { zones: "getZones" }),
        ...mapGetters({ getBrand: "brand/getBrand" }),
        iconColor() {
            return getStatusColor(this.forecast.deleted ? "DELETE" : this.forecast.status);
        },
        formDate(date) {
            return fDate(date);
        },
        isForecastManager() {
            return this.hasAccessByProfile(PROFILES.FORECAST_MANAGER);
        },
        isForecastContributor() {
            return this.hasAccessByProfile(PROFILES.ZONE_FORECASTER) || this.hasAccessByProfile(PROFILES.ZONE_READER);
        },
        isZonesSupervisor() {
            return this.hasAccessByProfile(PROFILES.ZONES_SUPERVISOR);
        },
        statusName() {
            const name = this.forecast ? getStatusName(this.forecast.deleted ? "DELETE" : this.forecast.status) : "";
            if (name.length > 35) {
                return name.substring(0, 5) + "...";
            }
            return name;
        },
        statusColor() {
            return this.forecast ? getStatusColor(this.forecast.deleted ? "DELETE" : this.forecast.status) : "";
        },
        statusBgColor() {
            return this.forecast ? getStatusBgColor(this.forecast.deleted ? "DELETE" : this.forecast.status) : "";
        },
        marketsSubmitted() {
            return `${this.forecast.numberOfSubmittedForecastWindows}/${this.markets}`;
        },
        markets() {
            return this.forecast.numberOfZones;
        },
        items() {
            return this.forecast.numberOfItems;
        },
        isApproved() {
            return this.forecast.isTransmissionApproved === true;
        },
        isXlsExist() {
            return !!(
                this.forecast.status !== FORECAST_STATUS.DRAFT &&
                this.forecast.status !== FORECAST_STATUS.PENDING &&
                this.forecast.deleted !== true
            );
        },
        displayXlsExportReader() {
            return !!this.displayXlsExport;
        }
    },
    methods: {
        makeLink() {
            if (this.window != null) {
                return this.$router.push(`${ROUTES.BASE}/window/${this.window.id}`);
            }
            return this.$router.push(`${ROUTES.BASE}/${this.forecast.id}`);
        },
        addSelect(checked) {
            this.selected = checked;
            this.$emit("check", this.forecast.id);
        },
        async exportXls(forecast) {
            this.$emit("updateLoading", true);
            const resp = await this.tryLoad(() => forecastsService.getPathForExportXLS([forecast.id]));
            const a = document.createElement("a");
            a.href = URL.createObjectURL(new Blob([resp], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
            a.setAttribute(
                "download",
                this.getBrand.name +
                    "_Forecast_" +
                    forecast.reference +
                    "_Summary_" +
                    Globals.moment(new Date()).format("YYYY-MM-DD-HH-mm-ss-SSS") +
                    ".xlsx"
            );
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            this.$emit("updateLoading", false);
        },
        isSelected() {
            return !!this.forecastSelected.find(f => f.id === this.forecast.id);
        }
    }
};
</script>

<style scoped lang="scss">
tr {
    border: 10px solid white;
    cursor: pointer;
    &:hover,
    &:active {
        td.id {
            border-left-color: black;
        }
    }
    td {
        overflow-wrap: break-word;
        position: static !important;
        &.ref {
            position: relative !important;
            padding-right: 20px;
            &:after {
                content: "•";
                position: absolute;
                right: -5px;
                top: 50%;
                transform: translateY(-50%) translateX(-10px);
                font-size: 25px;
            }
        }
        padding: 7px 0;
        font-size: 22px;
        color: black;
        font-family: Oswald, Sans-Serif;
        transition: 0.3s ease-in-out;

        @media (max-width: 1440px) {
            font-size: 18px;
        }

        &.id {
            border-left: 2px solid transparent;
        }
        &.calendar,
        &.markets,
        &.items,
        &.approved,
        &.xls,
        &.status {
            text-align: center;
        }
        &:not(.status) {
            font-weight: 500;
        }
        &:not(.calendar) {
            text-transform: uppercase;
        }
        &.status {
            color: white;
            @media (max-width: 1440px) {
                font-size: 16px;
            }
        }
        &.check {
            background-color: white;
        }
        &.id {
            text-align: center;
            vertical-align: middle;
            div {
                margin: auto;
                width: 20px;
                height: 20px;
                background-color: white;
                border-radius: 100%;
                display: flex;
                justify-content: center;
                align-items: center;
                span {
                    font-size: 9px;
                    font-weight: 400;
                }
            }
        }
    }
}
</style>



// WEBPACK FOOTER //
// ForecastDetailBanner.vue?6bd4aa8e